import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './StateChangeOperations.scss';
import { productApiClient } from '../../../../clients/ProductAPIClient';
import { IAlbum } from '../../../../data/models/album';
import { IValidationResults } from '../../../../data/models/validation';
import { AlbumState } from '../../../../data/models/albumState';
import { useSelector } from 'react-redux';
import { ILoadAlbum } from '../../../../state/actions/albumAction';
import store from '../../../../state/store';
import { getAuth } from '../../../../selectors/auth';
import ConfirmationModal from '../../../common/ConfirmationModal/ConfirmationModal';
import useAuthUtilities from '../../../../AuthMiddleware/AuthUtilities';
import { logger } from '../../../../utils/Logger';
import RejectionModal from '../../../common/RejectionModal/RejectionModal';
import useProductStateTransition from '../../../../hooks/useProductStateTransition';
import { FormatType } from '@/data/models/formatType';
import Spinner, { SpinnerSize } from '@/components/common/Spinner/Spinner';
import {
  getValidationIssuesFromError,
  getValidationIssuesFromPayload,
  isValidationError,
} from '@/utils/validationErrors';
import { IChangeRequest } from '@/data/models/changeRequest';

interface Props {
  album: IAlbum;
  setErrorBanner: (errorBanner: boolean) => void;
  setErrors: (errors: IValidationResults[]) => void;
  setApiError?: (error: string | null) => void;
  formatType: FormatType;
  submissionsGridUrl: string;
  changeRequest?: IChangeRequest;
  hasChangeRequest?: boolean;
}

const StateChangeOperations = ({
  album,
  setErrorBanner,
  setErrors,
  setApiError,
  formatType,
  submissionsGridUrl,
  changeRequest,
  hasChangeRequest,
}: Props) => {
  const { t } = useTranslation();
  const { hasWriteAccess } = useSelector(getAuth);
  const loadAlbumStore: ILoadAlbum = store.getState().loadAlbumState;
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationModalBody, setConfirmationModalBody] = useState('');
  const [confirmationModalHeader, setConfirmationModalHeader] = useState('');
  const [confirmationModalAction, setConfirmationModalAction] = useState(
    () => () => {
      return Promise.resolve();
    }
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [
    isSubmitForImportValidationInProgress,
    setIsSubmitForImportValidationInProgress,
  ] = useState(false);
  const submitForImportValidationInFlight = useRef(false);
  const { canUserSeeSubmitForImportButton, canUserSeeRejectButton } =
    useAuthUtilities();
  const { updateProductState, updateChangeRequestState } =
    useProductStateTransition(submissionsGridUrl);

  const isInDraftState = album.albumState === AlbumState.Draft;
  const isInRejectedState = album.albumState === AlbumState.Rejected;
  //will be adding user details in this context
  const isInReviewState = album.albumState === AlbumState.UnderReview;
  const isInPendingImportState = album.albumState === AlbumState.PendingImport;
  const isInChangeRequestState = !!changeRequest;

  const handleStateChange = async (newState: AlbumState) => {
    const includeSubmitValidations =
      newState === AlbumState.PendingImport &&
      album.albumState !== AlbumState.PendingImport;
    if (includeSubmitValidations && submitForImportValidationInFlight.current) {
      return;
    }

    if (includeSubmitValidations) {
      submitForImportValidationInFlight.current = true;
      setIsSubmitForImportValidationInProgress(true);
    }

    const albumJson = JSON.stringify(album);
    const changeRequestValidationId =
      changeRequest?.id ?? album.activeChangeRequestId;
    productApiClient
      .validateAlbum(
        albumJson,
        includeSubmitValidations,
        changeRequestValidationId
      )
      .then((response: any) => {
        const validationIssues = getValidationIssuesFromPayload(response);
        const validationErrors = validationIssues.filter(isValidationError);
        if (validationErrors.length === 0) {
          setErrors(validationIssues);
          setErrorBanner(validationIssues.length > 0);
          if (newState == AlbumState.UnderReview) {
            setConfirmationModalBody(
              t('product.editProduct.submitForReviewModalBody')
            );
            setConfirmationModalHeader(
              t('product.editProduct.submitForReviewModalHeader')
            );
          }
          if (newState == AlbumState.PendingImport) {
            setConfirmationModalBody(
              t('product.editProduct.submitForImportModalBody')
            );
            setConfirmationModalHeader(
              t('product.editProduct.submitForImportModalHeader')
            );
          } else if (newState == AlbumState.Released) {
            setConfirmationModalBody(
              t('product.editProduct.releaseProductModalBody')
            );
            setConfirmationModalHeader(
              t('product.editProduct.releaseProductModalHeader')
            );
          }
          setConfirmationModalAction(() => async () => {
            setIsSubmitting(true);
            try {
              if (setApiError) setApiError(null);
              if (isInChangeRequestState) {
                await updateChangeRequestState({
                  albumId: changeRequest.albumId,
                  changeRequestId: changeRequest.id!,
                  albumTitle: album.title,
                  newState,
                  nonce: changeRequest.nonce,
                });
              } else {
                await updateProductState({
                  albumId: album.id!,
                  albumTitle: album.title,
                  newState,
                });
              }
              setIsSubmitting(false);
              setShowConfirmationModal(false);
            } catch (error: any) {
              const validationIssues = getValidationIssuesFromError(error);
              if (validationIssues.length > 0) {
                setErrors(validationIssues);
                setErrorBanner(true);
                if (setApiError) setApiError(null);
                setIsSubmitting(false);
                setShowConfirmationModal(false);
                return;
              }

              if (setApiError) {
                const errorMessage =
                  error?.response?.data?.message ||
                  (typeof error?.response?.data === 'string'
                    ? error.response.data
                    : null) ||
                  error?.message ||
                  t('product.stateTransition.unexpectedError');
                setApiError(errorMessage);
              }
              setIsSubmitting(false);
              setShowConfirmationModal(false);
            }
          });
          setShowConfirmationModal(true);
        } else {
          setErrors(validationIssues);
          setErrorBanner(true);
        }
      })
      .catch((error: any) => {
        logger.error(error);
      })
      .finally(() => {
        if (includeSubmitValidations) {
          submitForImportValidationInFlight.current = false;
          setIsSubmitForImportValidationInProgress(false);
        }
      });
  };

  const handleRejection = () => {
    setShowRejectionModal(true);
  };

  return (
    <div>
      <ConfirmationModal
        showModal={showConfirmationModal}
        setShowModal={setShowConfirmationModal}
        message={
          isSubmitting ? (
            <div className="state-change-operations-spinner">
              <Spinner size={SpinnerSize.Normal} />
            </div>
          ) : (
            confirmationModalBody
          )
        }
        header={confirmationModalHeader}
        handleConfirm={confirmationModalAction}
        confirmButtonDisabled={isSubmitting}
      ></ConfirmationModal>
      {showRejectionModal ? (
        <RejectionModal
          albumId={album.id!}
          albumTitle={album.title}
          showModal={showRejectionModal}
          setShowModal={setShowRejectionModal}
          formatType={formatType}
        ></RejectionModal>
      ) : null}
      <div className="stateChangeOperations-btngrp">
        {isInReviewState && hasWriteAccess && canUserSeeRejectButton() && (
          <button
            className="btn btn-danger stateChangeOperations-btn"
            onClick={() => handleRejection()}
          >
            <span className="text-center">
              {' '}
              {t('productActions.button.reject')}{' '}
            </span>
          </button>
        )}

        {(isInReviewState || isInPendingImportState) &&
          hasWriteAccess &&
          canUserSeeSubmitForImportButton() && (
            <button
              className="btn btn-primary stateChangeOperations-btn"
              onClick={() => handleStateChange(AlbumState.PendingImport)}
              disabled={
                isSubmitForImportValidationInProgress || isInPendingImportState
              }
            >
              <span className="text-center">
                {' '}
                {t('productActions.button.submitForImport')}{' '}
              </span>
            </button>
          )}

        {isInChangeRequestState &&
          hasWriteAccess &&
          canUserSeeSubmitForImportButton() && (
            <button
              className="btn btn-primary stateChangeOperations-btn"
              onClick={() => handleStateChange(AlbumState.PendingImport)}
              disabled={
                changeRequest?.state === AlbumState.Error || !hasChangeRequest
              }
            >
              <span className="text-center">
                {' '}
                {t('productActions.button.submitChangeRequest')}{' '}
              </span>
            </button>
          )}

        {(isInDraftState || isInRejectedState) && hasWriteAccess && (
          <button
            className="btn btn-primary stateChangeOperations-btn"
            onClick={() => handleStateChange(AlbumState.UnderReview)}
            disabled={loadAlbumStore.isJustCreated === true}
          >
            <span className="text-center">
              {' '}
              {t('productActions.button.submitForReview')}{' '}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default StateChangeOperations;
